import { inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withMethods,
  withState,
} from '@ngrx/signals';
import {
  LegalAcknowledgement,
  LegalAcknowledgementService,
} from '../services/legal-acknowledgement.service';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AcknowledgementType } from 'src/app/core/enums/acknowledgement-type.enum';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store';
import { selectParticipantId } from 'src/app/features/session/store/selectors/session.selectors';

interface LegalAcknowledgementsState {
  acknowledgements: LegalAcknowledgement[];
  activeAcknowledgementDetails: LegalAcknowledgement | null;
  acknowledgementCache: Record<string, LegalAcknowledgement>;
  isSubmitting: boolean;
  isAcknowledgementLoading: boolean;
  error: string | null;
  successMessage: string | null;
}

const initialState: LegalAcknowledgementsState = {
  acknowledgements: [],
  activeAcknowledgementDetails: null,
  acknowledgementCache: {},
  isSubmitting: false,
  isAcknowledgementLoading: false,
  error: null,
  successMessage: null,
};

export const LegalAcknowledgementsStore = signalStore(
  withState(initialState),
  withMethods(
    (
      store,
      legalAcknowledgementService = inject(LegalAcknowledgementService),
      appStore = inject(Store<AppState>),
    ) => ({     
        async loadAcknowledgementDetails(
          acknowledgementType: AcknowledgementType,
        forceReload = false,
        ): Promise<LegalAcknowledgement> {
          const cachedAcknowledgement =
            store.acknowledgementCache()[acknowledgementType];
          if (cachedAcknowledgement && !forceReload) {
            patchState(store, {
              activeAcknowledgementDetails: cachedAcknowledgement,
              isAcknowledgementLoading: false,
              error: null,
            });
            return cachedAcknowledgement;
          }

          patchState(store, { isAcknowledgementLoading: true, error: null });

          const currentParticipantId = await firstValueFrom(
            appStore.select(selectParticipantId).pipe(filter((id) => !!id)),
          );

          try {
            const acknowledgementDetails = await firstValueFrom(
              legalAcknowledgementService.getAcknowledgementDetails(
                acknowledgementType,
                currentParticipantId,
              ),
            );

            if (!acknowledgementDetails) {
              throw new Error(
                `Legal acknowledgement '${acknowledgementType}' not found`,
              );
            }

            patchState(store, {
              activeAcknowledgementDetails: acknowledgementDetails,
              acknowledgementCache: {
                ...store.acknowledgementCache(),
                [acknowledgementType]: acknowledgementDetails,
              },
              isAcknowledgementLoading: false,
              error: null,
            });

            return acknowledgementDetails;
          } catch (error) {
            const errorMessage =
              'Failed to load legal acknowledgement details. Please try again.';
            patchState(store, {
              isAcknowledgementLoading: false,
              error: errorMessage,
            });
            throw error;
          }
        },

        async acceptAcknowledgements(
          acknowledgementTypes: AcknowledgementType[],
        ): Promise<void> {
          patchState(store, { isSubmitting: true, error: null });

          const currentParticipantId = await firstValueFrom(
            appStore.select(selectParticipantId).pipe(filter((id) => !!id)),
          );

          try {
            const result = await firstValueFrom(
              legalAcknowledgementService.acceptAcknowledgements(
                currentParticipantId,
                acknowledgementTypes,
              ),
            );

            if (result.success) {
              patchState(store, {
                isSubmitting: false,
                successMessage: result.message,
                error: null,
              });
            } else {
              throw new Error(result.message || 'Submission failed');
            }
          } catch (error) {
            const errorMessage =
              'Failed to submit acknowledgements. Please try again.';
            patchState(store, {
              isSubmitting: false,
              error: errorMessage,
              successMessage: null,
            });
            throw error;
          }
        },

        clearError() {
          patchState(store, { error: null });
        },      
    }),
  ),
);
